/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import HuggingFaceUploadModal from '../../components/admin/HuggingFaceUploadModal';
import HuggingFaceSyncModal from '../../components/admin/HuggingFaceSyncModal';
import './AdapterManagement.css';

const AdapterManagement = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();

  // State
  const [adapters, setAdapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const [syncing, setSyncing] = useState(false);
  const [showHFUploadModal, setShowHFUploadModal] = useState(false);
  const [showHFSyncModal, setShowHFSyncModal] = useState(false);

  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [autoSyncLoading, setAutoSyncLoading] = useState(false);


  /**
   * Format domain name with proper casing
   * WMS, iSeries, Civil, Stitching, Platform
   */
  const formatDomainName = (domain) => {
    if (!domain) return 'Unknown';
    
    const domainLower = domain.toLowerCase();
    
    // Special cases
    const domainMap = {
      'wms': 'WMS',
      'iseries': 'iSeries',
      'civil': 'Civil',
      'stitching': 'Stitching',
      'platform': 'Platform'
    };
    
    return domainMap[domainLower] || domain.charAt(0).toUpperCase() + domain.slice(1).toLowerCase();
  };

  /**
   * Load adapters
   */
  useEffect(() => {
    loadAdapters();
  }, [filter]);

  const loadAdapters = async () => {
    try {
      setLoading(true);
      setError('');

      const filters = {};
      if (filter === 'active') filters.active = true;
      if (filter === 'inactive') filters.active = false;

      // adminApi.getAdapters already returns response.data
      // So data here is directly: { success, count, adapters, grouped }
      const data = await adminApi.getAdapters(filters);
      console.log('[AdapterManagement] Adapters data:', data);
      setAdapters(data.adapters || []);

    } catch (err) {
      console.error('Load adapters error:', err);
      setError(err.response?.data?.message || 'Failed to load adapters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  loadAutoSyncSetting();
}, []);

const loadAutoSyncSetting = async () => {
  try {
    const response = await adminApi.get('/system-config/adapter_sync_auto_enabled');
    setAutoSyncEnabled(response.data?.value === true);
  } catch (err) {
    setAutoSyncEnabled(false);
  }
};

const handleAutoSyncToggle = async () => {
  try {
    setAutoSyncLoading(true);
    const newValue = !autoSyncEnabled;
    await adminApi.put('/system-config/adapter_sync_auto_enabled', { value: newValue });
    setAutoSyncEnabled(newValue);
  } catch (err) {
    alert('Failed to update setting');
  } finally {
    setAutoSyncLoading(false);
  }
};

  /**
   * Handle activate adapter
   */
  const handleActivate = async (adapterId) => {
    if (!window.confirm('Activate this adapter? It will be available for inference.')) {
      return;
    }

    try {
      await adminApi.activateAdapter(adapterId);
      loadAdapters(); // Reload adapters
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to activate adapter');
    }
  };

  /**
   * Handle deactivate adapter
   */
  const handleDeactivate = async (adapterId) => {
    if (!window.confirm('Deactivate this adapter? It will no longer be available for inference.')) {
      return;
    }

    try {
      await adminApi.deactivateAdapter(adapterId);
      loadAdapters(); // Reload adapters
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to deactivate adapter');
    }
  };

  /**
   * Handle sync adapters from filesystem
   */
  const handleSyncAdapters = async () => {
    if (!window.confirm('Sync adapters from filesystem? This will scan the models directory and create metadata records for any new adapters.')) {
      return;
    }

    try {
      setSyncing(true);
      setError('');
      const result = await adminApi.syncAdapters();
      alert(`✅ Sync completed! Found ${result.adapterCount} adapters in database.`);
      loadAdapters(); // Reload adapters
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync adapters');
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Format file size
   */
  const formatSize = (bytes) => {
    if (!bytes) return '--';
    const mb = bytes / (1024 * 1024);
    if (mb > 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  /**
   * Format duration between start and end dates
   */
  const formatDuration = (startDate, endDate, status) => {
    if (!startDate) return '--';

    const start = new Date(startDate);
    let end;

    // For completed jobs, use the completion time
    if (status === 'completed' && endDate) {
      end = new Date(endDate);
    }
    // For in-progress jobs, calculate duration up to now
    else if (['training', 'uploading_dataset', 'submitting_training', 'preparing_data', 'in_progress'].includes(status)) {
      end = new Date();
    }
    // For failed/pending jobs without end time
    else if (status === 'failed' && endDate) {
      end = new Date(endDate);
    }
    else {
      return '--';
    }

    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;

    if (diffHours > 0) {
      return `${diffHours}h ${remainingMins}m`;
    } else if (diffMins > 0) {
      return `${diffMins}m`;
    } else {
      return '< 1m';
    }
  };

  return (
    <AdminLayout showBackButton={true} backPath="/admin/dashboard">
      <div className="adapter-management">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <div>
              <h1>Adapter Management</h1>
              <p className="page-subtitle">Manage trained LoRA/QLoRA adapters</p>
            </div>
          </div>
          <div className="header-right">
            {/* Adapter Upload to Modal */}
            <button
              className="upload-button"
              onClick={() => navigate('/admin/adapter-upload')}
              title="Upload adapters to Modal via HuggingFace"
            >
              📤 Upload to Modal
            </button>
            {/* Manual Upload to HuggingFace */}
            <button
              className="hf-upload-button"
              onClick={() => setShowHFUploadModal(true)}
              title="Upload adapter to HuggingFace manually"
              style={{ backgroundColor: '#f59e0b', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              🤗 Upload to HuggingFace
            </button>
            {/* Sync HF to Modal */}
            <button
              className="hf-sync-button"
              onClick={() => setShowHFSyncModal(true)}
              title="Sync adapter from HuggingFace to Modal"
              style={{ backgroundColor: '#8b5cf6', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              🔄 Sync HF → Modal
            </button>
            {/* Phase 2B: Adapter Hierarchy Button */}
            <button
              className="hierarchy-button"
              onClick={() => navigate('/admin/adapter-hierarchy')}
              title="Configure adapter hierarchy and fallback chain"
            >
              🧬 Adapter Hierarchy
            </button>
            {/* Sync from filesystem button */}
            <button
              className="sync-button"
              onClick={handleSyncAdapters}
              disabled={syncing}
            >
              {syncing ? '⏳ Syncing...' : '🔄 Sync from Filesystem'}
            </button>
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* Auto-Sync Settings */}
        <div style={{
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span>⚙️ <strong>Auto-Sync:</strong></span>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoSyncEnabled}
              onChange={handleAutoSyncToggle}
              disabled={autoSyncLoading}
              style={{ marginRight: '8px' }}
            />
            Check HuggingFace every 5 min
          </label>
          <span style={{ color: autoSyncEnabled ? '#28a745' : '#6c757d', fontSize: '12px' }}>
            {autoSyncEnabled ? '✅ Enabled' : '⏸️ Disabled'}
          </span>
        </div>



        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={`tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Adapters
          </button>
          <button
            className={`tab ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active
          </button>
          <button
            className={`tab ${filter === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilter('inactive')}
          >
            Inactive
          </button>
        </div>

        {/* Adapters List */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p>Loading adapters...</p>
          </div>
        ) : adapters.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔌</div>
            <h3>No Adapters Found</h3>
            <p>
              {filter === 'all'
                ? 'No adapters have been trained yet. Submit a training job to create one.'
                : `No ${filter} adapters found.`}
            </p>
            <button
              className="primary-button"
              onClick={() => navigate('/admin/training/submit')}
            >
              Submit Training Job
            </button>
          </div>
        ) : (
          <div className="adapters-grid">
            {adapters.map(adapter => (
              <div key={adapter._id} className={`adapter-card ${adapter.active ? 'active' : 'inactive'}`}>
                {/* Card Header */}
                <div className="card-header">
                  <div className="domain-info">
                    {/* FIXED: Use formatDomainName for proper casing */}
                    <h3 className="domain-name">{formatDomainName(adapter.domain)}</h3>
                    <span className={`status-badge ${adapter.active ? 'badge-active' : 'badge-inactive'}`}>
                      {adapter.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {adapter.active && <div className="active-indicator"></div>}
                </div>

                {/* Card Body */}
                <div className="card-body">
                  {/* Version */}
                  <div className="info-row">
                    <span className="info-label">Version</span>
                    <span className="info-value">v{adapter.version}</span>
                  </div>

                  {/* Created */}
                  <div className="info-row">
                    <span className="info-label">Created</span>
                    <span className="info-value">{formatDate(adapter.createdAt)}</span>
                  </div>

                  {/* Submitted By */}
                  {adapter.createdBy && (
                    <div className="info-row">
                      <span className="info-label">Submitted By</span>
                      <span className="info-value">
                        {adapter.createdBy.fullName || adapter.createdBy.username || 'Unknown'}
                      </span>
                    </div>
                  )}

                  {/* Training Duration */}
                  {adapter.trainingJobId && (
                    <div className="info-row">
                      <span className="info-label">Training Duration</span>
                      <span className="info-value">
                        {formatDuration(
                          adapter.trainingJobId.createdAt,
                          adapter.trainingJobId.completedAt,
                          adapter.trainingJobId.status
                        )}
                      </span>
                    </div>
                  )}

                  {/* Size */}
                  {adapter.modelSize && (
                    <div className="info-row">
                      <span className="info-label">Size</span>
                      <span className="info-value">{formatSize(adapter.modelSize)}</span>
                    </div>
                  )}

                  {/* Training Job */}
                  {adapter.trainingJobId && (
                    <div className="info-row">
                      <span className="info-label">Training Job</span>
                      <button
                        className="job-link"
                        onClick={() => navigate(`/admin/training/jobs/${adapter.trainingJobId}`)}
                      >
                        View Job
                      </button>
                    </div>
                  )}

                  {/* Metrics */}
                  {adapter.quality && (
                    <div className="metrics-section">
                      <h4>Performance Metrics</h4>
                      <div className="metrics-grid">
                        {adapter.quality.testAccuracy !== undefined && (
                          <div className="metric-item">
                            <span className="metric-label">Accuracy</span>
                            <span className="metric-value">
                              {(adapter.quality.testAccuracy * 100).toFixed(2)}%
                            </span>
                          </div>
                        )}
                        {adapter.quality.overallScore !== undefined && (
                          <div className="metric-item">
                            <span className="metric-label">Overall Score</span>
                            <span className="metric-value">
                              {adapter.quality.overallScore.toFixed(0)}/100
                            </span>
                          </div>
                        )}
                        {adapter.quality.testsPassed !== undefined && adapter.quality.testsTotal !== undefined && (
                          <div className="metric-item">
                            <span className="metric-label">Tests Passed</span>
                            <span className="metric-value">
                              {adapter.quality.testsPassed}/{adapter.quality.testsTotal}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Card Actions */}
                <div className="card-actions">
                  {adapter.active ? (
                    hasPermission('adapter.deactivate') && (
                      <button
                        className="action-button deactivate-btn"
                        onClick={() => handleDeactivate(adapter._id)}
                      >
                        Deactivate
                      </button>
                    )
                  ) : (
                    hasPermission('adapter.activate') && (
                      <button
                        className="action-button activate-btn"
                        onClick={() => handleActivate(adapter._id)}
                      >
                        Activate
                      </button>
                    )
                  )}

                  <button
                    className="action-button details-btn"
                    onClick={() => navigate(`/admin/training/adapters/${adapter._id}`)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {!loading && adapters.length > 0 && (
          <div className="summary-stats">
            <div className="stat-box">
              <span className="stat-number">{adapters.length}</span>
              <span className="stat-text">
                {filter === 'all' ? 'Total Adapters' : `${filter} Adapters`}
              </span>
            </div>
            {filter === 'all' && (
              <>
                <div className="stat-box">
                  <span className="stat-number">
                    {adapters.filter(a => a.active).length}
                  </span>
                  <span className="stat-text">Active</span>
                </div>
                <div className="stat-box">
                  <span className="stat-number">
                    {adapters.filter(a => !a.active).length}
                  </span>
                  <span className="stat-text">Inactive</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* HuggingFace Upload Modal */}
        <HuggingFaceUploadModal
          isOpen={showHFUploadModal}
          onClose={() => setShowHFUploadModal(false)}
          onSuccess={() => {
            setShowHFUploadModal(false);
            loadAdapters();
          }}
        />

        {/* HuggingFace Sync Modal */}
        <HuggingFaceSyncModal
          isOpen={showHFSyncModal}
          onClose={() => setShowHFSyncModal(false)}
          onSuccess={() => {
            setShowHFSyncModal(false);
            loadAdapters();
          }}
        />
      </div>
    </AdminLayout>
  );
};

export default AdapterManagement;
